import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Briefcase, Download, Award, Headphones, Star, Search, Crown, ArrowLeft, ArrowRight, Users, Clock, TrendingUp, PlayCircle } from 'lucide-react';
import { useLang, tx } from '../i18n/LanguageContext';
import { useCategories } from '../hooks/useCategories';
import { heroImage, stats } from '../data/mock';
import api from '../lib/api';
import { getMergedCourses } from '../lib/courses';

const statIcons = { students: Users, courses: BookOpen, hours: Clock, projects: TrendingUp };

const HeroSearch = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const { categories, getLabel } = useCategories();
  const [courseCats, setCourseCats] = useState(null);
  const [q, setQ] = useState('');
  const L = (ar, en) => tx(lang, ar, en);
  useEffect(() => {
    api.get('/courses').then(({ data }) => {
      setCourseCats(new Set(getMergedCourses(data).flatMap(c => (Array.isArray(c.categories) && c.categories.length ? c.categories : ['general']))));
    }).catch(() => setCourseCats(new Set()));
  }, []);
  const chips = courseCats ? categories.filter(c => courseCats.has(c.value)).slice(0, 6) : [];
  const submit = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/courses?q=${encodeURIComponent(q.trim())}` : '/courses');
  };
  return (
    <div className="mb-8">
      <form onSubmit={submit} className="flex items-center gap-2 bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-200/60 p-2 max-w-xl focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-100 transition-[border-color,box-shadow]">
        <Search className="w-5 h-5 text-slate-400 ms-2 shrink-0" />
        <input
          data-testid="hero-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={L('ابحث عن كورس: PLC، SCADA، HMI...', 'Search courses: PLC, SCADA, HMI...')}
          className="flex-1 min-w-0 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-sm sm:text-base py-2"
        />
        <button data-testid="hero-search-btn" type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-4 sm:px-6 py-2.5 rounded-xl transition-colors shrink-0">
          {L('بحث', 'Search')}
        </button>
      </form>
      <div className="flex flex-wrap gap-2 mt-3">
        {chips.map((c) => (
          <Link
            key={c.value}
            to={`/courses?cat=${encodeURIComponent(c.value)}`}
            data-testid={`hero-cat-${c.value}`}
            className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:border-blue-400 hover:text-blue-700 hover:bg-blue-50 transition-colors"
          >
            {getLabel(c.value, lang)}
          </Link>
        ))}
      </div>
    </div>
  );
};

const HeroStats = () => {
  const { t, lang } = useLang();
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 mt-10 sm:mt-14">
      {stats.map((s, i) => {
        const Ic = statIcons[s.key];
        return (
          <div key={s.key} data-testid={`hero-stat-${s.key}`} className="fade-up bg-white rounded-2xl border border-slate-200/80 p-4 sm:p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-[transform,box-shadow] duration-300" style={{ animationDelay: `${i * 80}ms` }}>
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-3"><Ic className="w-5 h-5 text-blue-600" /></div>
            <div className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight" dir="ltr">{tx(lang, s.value, s.value)}</div>
            <div className="text-xs sm:text-sm font-semibold text-slate-500 mt-1">{t.stats[s.key]}</div>
          </div>
        );
      })}
    </div>
  );
};

const FeatureList = () => {
  const { t } = useLang();
  const items = [
    [BookOpen, t.features.f1Title, t.features.f1Desc],
    [Briefcase, t.features.f2Title, t.features.f2Desc],
    [Download, t.features.f3Title, t.features.f3Desc],
    [Award, t.features.f4Title, t.features.f4Desc],
    [Headphones, t.features.f5Title, t.features.f5Desc],
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-5 mt-8 pt-7 border-t border-slate-200">
      {items.map(([Ic, title, desc]) => (
        <div key={title} className="flex items-start gap-3">
          <Ic className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-slate-900 text-sm font-bold mb-0.5">{title}</div>
            <div className="text-slate-500 text-xs leading-relaxed">{desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Hero = () => {
  const { t, isRTL, lang } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const L = (ar, en) => tx(lang, ar, en);

  return (
    <section data-testid="home-hero" className="relative pt-[5.5rem] sm:pt-24 pb-8 sm:pb-10 overflow-hidden bg-gradient-to-b from-blue-50/80 via-slate-50 to-slate-50">
      <div className="absolute inset-0 hero-grid pointer-events-none" />
      <div className="site-container relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-bold text-xs mb-6">
              <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
              <span>{t.hero.badge}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-black text-slate-900 leading-[1.15] tracking-tight mb-6">
              {t.hero.title1} <span className="text-blue-600">{t.hero.title2}</span> {t.hero.title3}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mb-8">{t.hero.desc}</p>

            <HeroSearch />

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/subscription" data-testid="hero-subscribe-cta" className="group btn-subscribe px-6 h-12">
                <Crown className="w-5 h-5" />
                {L('اشترك الآن وابدأ التعلم', 'Subscribe & start learning')}
                <Arrow className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to="/courses" data-testid="hero-courses-cta" className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-slate-800 font-bold px-6 h-12 rounded-xl border border-slate-300 transition-colors">
                <PlayCircle className="w-5 h-5 text-blue-600" />
                {t.hero.cta1}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: '150ms' }}>
            <div className="relative rounded-3xl bg-white p-2.5 border border-slate-200 shadow-2xl shadow-slate-300/50">
              <img src={heroImage} alt="Industrial Automation" className="w-full aspect-[4/3] lg:aspect-[1/1] object-cover rounded-2xl" />
            </div>
            <div className="absolute -bottom-5 start-4 sm:-start-6 bg-white rounded-2xl border border-slate-200 shadow-xl px-4 py-3 flex items-center gap-3 float-slow">
              <div className="flex -space-x-2 rtl:space-x-reverse">
                {['bg-blue-500', 'bg-amber-500', 'bg-emerald-500'].map((c, i) => (
                  <div key={c} className={`w-8 h-8 rounded-full ${c} border-2 border-white flex items-center justify-center text-white text-[11px] font-bold`}>{'AMS'[i]}</div>
                ))}
              </div>
              <div>
                <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}</div>
                <div className="text-xs text-slate-600"><span className="font-black text-slate-900">{tx(lang, '+15K', '+15K')}</span> {L('طالب يثقون بنا', 'students trust us')}</div>
              </div>
            </div>
          </div>
        </div>

        <HeroStats />
        <FeatureList />
      </div>
    </section>
  );
};

export default Hero;
